'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import styles from './sticky-quick-nav.module.css'

function readScrollMarginTopPx(el: Element): number {
  const raw = getComputedStyle(el).scrollMarginTop.trim()
  const n = Number.parseFloat(raw)
  if (!Number.isFinite(n)) return 0
  if (raw.endsWith('px')) return n
  if (raw.endsWith('rem')) {
    const rootPx = Number.parseFloat(getComputedStyle(document.documentElement).fontSize)
    return n * (Number.isFinite(rootPx) ? rootPx : 16)
  }
  return n
}

/** Matches `sectionScrollTarget` scroll-margin (DS calc on `.page`). */
function getActivationTopPx(navItems: ReadonlyArray<{ id: string }>): number {
  return (
    navItems
      .map(item => document.getElementById(item.id))
      .filter((el): el is Element => Boolean(el))
      .map(readScrollMarginTopPx)
      .find(px => px > 0) ?? 0
  )
}

export type StickyNavItem = {
  id: string
  label: string
  /** Figma: last two use rgba(10,10,10,0.6) */
  muted?: boolean
}

/** Labels match main-column headings; order matches `page.tsx` doc column. */
export const BIKE_INSURANCE_STICKY_NAV_ITEMS: StickyNavItem[] = [
  { id: 'cover-options', label: 'Choose the right coverage' },
  { id: 'inclusions-exclusions', label: "What's covered & excluded" },
  { id: 'add-ons', label: 'Useful add-ons' },
  { id: 'calculator', label: 'Calculate your premium' },
  { id: 'why-acko', label: 'Why buy direct from ACKO' },
  { id: 'electric-bike', label: 'Electric bike insurance' },
  { id: 'trust-reviews', label: 'Why millions trust ACKO' },
  { id: 'claim-stories', label: 'How we earn your trust' },
  { id: 'buy-new', label: 'How to buy bike insurance' },
  { id: 'renew-bike', label: 'How to renew bike insurance' },
  { id: 'file-claim', label: 'How to file a claim' },
  { id: 'acko-vs-others', label: 'ACKO vs other insurers' },
  { id: 'premium-rates', label: 'TP premium rates by CC' },
  { id: 'premium-factors', label: 'What impacts your premium' },
  { id: 'faq', label: 'Frequently asked questions', muted: true },
  { id: 'learn-more', label: 'Learn more about bike insurance', muted: true },
]

type StickyQuickNavProps = {
  items?: readonly StickyNavItem[]
}

/**
 * Figma node 8458:9895 — left TOC: title + links (18px/28 medium #0a0a0a),
 * links 14px/20, gap-16, pl-16 pr-32, active = medium + primary + 2px bar.
 */
function computeActiveSectionId(navItems: readonly StickyNavItem[]): string {
  const focusY = window.scrollY + getActivationTopPx(navItems)
  const withTops = navItems
    .map(item => {
      const el = document.getElementById(item.id)
      return el ? { id: item.id, top: el.getBoundingClientRect().top + window.scrollY } : null
    })
    .filter((row): row is { id: string; top: number } => Boolean(row))

  if (withTops.length === 0) return navItems[0]?.id ?? ''

  withTops.sort((a, b) => a.top - b.top)
  return withTops.reduce((active, row) => (row.top <= focusY + 0.5 ? row.id : active), withTops[0].id)
}

export const StickyQuickNav = ({ items = BIKE_INSURANCE_STICKY_NAV_ITEMS }: StickyQuickNavProps) => {
  const [activeId, setActiveId] = useState<string>(items[0]?.id ?? '')
  const listRef = useRef<HTMLDivElement>(null)
  const linkRefs = useRef(new Map<string, HTMLAnchorElement>())

  useEffect(() => {
    const onScrollOrResize = () => {
      setActiveId(computeActiveSectionId(items))
    }

    onScrollOrResize()

    let ticking = false
    const onScroll = () => {
      if (!ticking) {
        ticking = true
        requestAnimationFrame(() => {
          onScrollOrResize()
          ticking = false
        })
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onScrollOrResize, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [items])

  useEffect(() => {
    const list = listRef.current
    const activeLink = linkRefs.current.get(activeId)
    if (!list || !activeLink) return

    const listRect = list.getBoundingClientRect()
    const activeLinkRect = activeLink.getBoundingClientRect()
    const revealOffset = 8

    if (activeLinkRect.top < listRect.top) {
      list.scrollBy({ top: activeLinkRect.top - listRect.top - revealOffset, behavior: 'smooth' })
    } else if (activeLinkRect.bottom > listRect.bottom) {
      list.scrollBy({ top: activeLinkRect.bottom - listRect.bottom + revealOffset, behavior: 'smooth' })
    }
  }, [activeId])

  const onClick = useCallback((e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  return (
    <aside className={styles.root}>
      <nav className={styles.nav} aria-label="On this page">
        <p className={styles.title}>On this page</p>
        <div ref={listRef} className={styles.list}>
          {items.map(item => {
            const isActive = activeId === item.id
            const linkClass = [styles.link, isActive ? styles.linkActive : '', item.muted ? styles.linkMuted : '']
              .filter(Boolean)
              .join(' ')
            return (
              <a
                key={item.id}
                ref={element => {
                  if (element) linkRefs.current.set(item.id, element)
                  else linkRefs.current.delete(item.id)
                }}
                href={`#${item.id}`}
                className={linkClass}
                onClick={e => onClick(e, item.id)}>
                {item.label}
              </a>
            )
          })}
        </div>
      </nav>
    </aside>
  )
}
